import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, apiFetch } from "@/context/AuthContext";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Sparkles, Cpu, Database, ShieldCheck, CreditCard, HardDrive,
  Bell, Server, FileCode2, Download, Github, CheckCircle2, AlertTriangle, XCircle,
  Rocket, FolderTree, Play, GitBranch,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import AttentionPulse from "@/modules/os/AttentionPulse";
import WorkspaceSwitcher from "@/components/nav/WorkspaceSwitcher";
import MissionControlButton from "@/components/nav/MissionControlButton";
import DeployPanel from "@/components/DeployPanel";
import ArchReviewPanel from "@/components/ArchReviewPanel";
import ProjectPlanPanel from "@/components/ProjectPlanPanel";
import GlobalReadinessPanel from "@/components/GlobalReadinessPanel";
import PublishWorldwide from "@/components/PublishWorldwide";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/architect`;

async function j(path, method = "GET", body) {
  const token = localStorage.getItem("arca_token");
  const res = await fetch(`${API}${path}`, {
    method, headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) { let d = "Request failed"; try { d = (await res.json()).detail || d; } catch { /* ignore */ } throw new Error(d); }
  return res.json();
}

const ARCH_ICONS = {
  frontend: Cpu, backend: Server, database: Database, authentication: ShieldCheck,
  payments: CreditCard, file_storage: HardDrive, notifications: Bell, deployment: Rocket,
};

export default function Studio() {
  const { projectId } = useParams();
  return projectId ? <ProjectView key={projectId} projectId={projectId} /> : <StudioHome />;
}

export function Shell({ children, right }) {
  return (
    <div className="min-h-screen relative">
      <div className="pointer-events-none absolute -top-40 right-0 w-[42rem] h-[42rem] bg-[#0055FF]/10 blur-[150px] rounded-full" />
      <header className="relative z-30 max-w-6xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="studio-home-logo">
            <div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg">A</div>
            <span className="font-display text-xl font-bold hidden sm:inline">ArcaCentum<span className="text-[#00F0FF]">.ai</span> <span className="text-slate-500 text-sm font-body">Studio</span></span>
          </Link>
          <WorkspaceSwitcher />
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <MissionControlButton className="hidden lg:inline-flex" />
          <AttentionPulse />
          <div className="hidden lg:block"><LanguageSwitcher /></div>
          <AccountNav />
          {right}
        </div>
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-6 pb-24">{children}</main>
    </div>
  );
}

function AccountNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (user === undefined) return null;
  if (!user) {
    return (
      <Link to="/login" data-testid="studio-login-btn" className="font-mono text-xs uppercase tracking-widest border border-white/15 rounded-full px-4 py-2 text-slate-200 hover:border-[#0055FF] hover:text-white transition-colors duration-300">
        Log in
      </Link>
    );
  }
  const label = (user.name || user.email || "?").trim();
  const initial = label[0]?.toUpperCase() || "?";
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="studio-account-btn" className="w-9 h-9 rounded-full bg-[#0055FF] text-white font-display font-bold flex items-center justify-center hover:ring-2 hover:ring-[#00F0FF]/50 transition-all duration-300">
        {initial}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 rounded-2xl border border-white/10 bg-[#0a0a0a] p-2 z-20 shadow-xl" data-testid="studio-account-menu">
            <div className="px-3 py-2 text-xs text-slate-500 truncate font-mono">{user.email}</div>
            <Link to="/account" onClick={() => setOpen(false)} data-testid="studio-account-link" className="block px-3 py-2 rounded-xl text-sm text-slate-200 hover:bg-white/5 transition-colors duration-200">Account</Link>
            <button onClick={() => { logout(); setOpen(false); navigate("/"); }} data-testid="studio-logout-btn" className="w-full text-left px-3 py-2 rounded-xl text-sm text-rose-300 hover:bg-white/5 transition-colors duration-200">Log out</button>
          </div>
        </>
      )}
    </div>
  );
}

function StudioHome() {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (user) { j("/projects").then(setProjects).catch(() => {}); apiFetch("/teams").then(setTeams).catch(() => {}); }
  }, [user, navigate]);

  const analyze = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    try {
      const s = await apiFetch("/discovery/start", { method: "POST", body: { idea: prompt.trim(), team_id: teamId || undefined } });
      navigate(`/studio/discovery/${s.session_id}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const EXAMPLES = ["An Airbnb-style rental marketplace with bookings & payments", "A Shopify-style multi-vendor store", "A LinkedIn-style professional network"];

  return (
    <Shell right={<Link to="/" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> {t("studio.home")}</Link>}>
      <div className="mb-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#00F0FF]"><Sparkles size={14} /> {t("studio.badge")}</div>
      <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tight leading-[0.95]">{t("studio.homeTitle1")}<br />{t("studio.homeTitle2")}</h1>
      <p className="text-slate-400 mt-5 max-w-2xl">{t("studio.homeSubtitle")}</p>

      <div className="mt-8 rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
        <textarea
          value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
          placeholder={t("studio.promptPlaceholder")}
          className="w-full resize-none bg-black/40 border border-white/10 rounded-2xl px-5 py-4 text-white outline-none focus:border-[#0055FF] transition-colors duration-300"
          data-testid="studio-prompt"
        />
        <div className="flex flex-wrap gap-2 mt-4">
          {EXAMPLES.map((e, i) => (
            <button key={i} onClick={() => setPrompt(e)} className="text-xs font-mono text-slate-400 border border-white/10 rounded-full px-3 py-1.5 hover:text-white hover:border-white/25 transition-colors duration-300" data-testid={`studio-example-${i}`}>{e}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <button onClick={analyze} disabled={busy || !prompt.trim()} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-7 py-4 rounded-2xl font-semibold shadow-[0_0_40px_rgba(0,85,255,0.5)] disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="studio-analyze-btn">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />} {busy ? "Starting…" : "Start Smart Discovery"}
          </button>
          {teams.length > 0 && (
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="bg-black/40 border border-white/10 rounded-2xl px-4 py-4 text-sm text-slate-300 outline-none focus:border-[#0055FF]" data-testid="studio-team-select">
              <option value="">{t("studio.personalProject")}</option>
              {teams.filter((t) => t.my_role !== "viewer").map((t) => <option key={t.id} value={t.id}>{`Team: ${t.name}`}</option>)}
            </select>
          )}
        </div>
      </div>

      {projects.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display font-bold text-xl mb-4">{t("studio.yourProjects")}</h2>
          <div className="grid md:grid-cols-3 gap-4" data-testid="studio-projects">
            {projects.map((p) => (
              <Link key={p.id} to={`/studio/${p.id}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 hover:border-[#0055FF] transition-colors duration-300" data-testid={`studio-project-${p.id}`}>
                <p className="font-display font-bold truncate">{p.name}</p>
                <p className="text-slate-500 text-xs mt-1 font-mono uppercase tracking-widest">{p.status} · {p.files_count} files</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}

function StatusBadge({ status }) {
  const map = { pending: "text-slate-500", in_progress: "text-[#00F0FF]", done: "text-emerald-400" };
  return <span className={`font-mono text-[10px] uppercase ${map[status] || "text-slate-500"}`}>{status.replace("_", " ")}</span>;
}

function ProjectView({ projectId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [activeFile, setActiveFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [qa, setQa] = useState(null);
  const [busy, setBusy] = useState("");
  const [ghToken, setGhToken] = useState("");
  const [ghUrl, setGhUrl] = useState(null);
  const [ghConnected, setGhConnected] = useState(false);

  const load = useCallback(async () => {
    const p = await j(`/projects/${projectId}`);
    setProject(p); setMilestones(p.milestones || []); setQa(p.qa || null); setGhUrl(p.github_url || null);
  }, [projectId]);

  // Redirect unauthenticated users to login, preserving return path
  useEffect(() => {
    if (user === null) {
      navigate(`/login?next=${encodeURIComponent(`/studio/${projectId}`)}`, { replace: true });
    }
  }, [user, projectId, navigate]);

  // Only load project data once we know the user is authenticated
  useEffect(() => {
    if (!user) return;
    setLoadError(null);
    load().catch((e) => setLoadError(e.message || "Could not load project"));
    apiFetch("/deploy/providers").then((d) => setGhConnected(!!d.providers.find((p) => p.key === "github" && p.connected))).catch(() => {});
  }, [user, load]);

  const arch = project?.architecture || {};

  const generate = async () => {
    try {
      const pc = await apiFetch("/wallet/precheck", { method: "POST", body: { action: "generate_project" } });
      if (!pc.ok) { toast.error(`Not enough credits — needs ${pc.cost}, you have ${pc.credits}. Top up in your account.`); return; }
      if (pc.would_exhaust || pc.low_after) toast.warning("Heads up: this build will use most of your remaining credits.");
    } catch { /* precheck is best-effort */ }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/generate`, { method: "POST", headers: { "Content-Type": "application/json", ...(localStorage.getItem("arca_token") ? { Authorization: `Bearer ${localStorage.getItem("arca_token")}` } : {}) }, body: JSON.stringify({ project_id: projectId }) });
      if (res.status === 401) { toast.error("Please log in to build."); return; }
      if (res.status === 402) { toast.error("Not enough credits — top up in your account."); return; }
      const reader = res.body.getReader();
      const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim(); if (!line) continue;
          const d = JSON.parse(line);
          if (d.milestone) setMilestones((ms) => ms.map((m, i) => i === d.milestone.index ? { ...m, status: d.milestone.status } : m));
          if (d.error) toast.error(d.error);
          if (d.done) { toast.success(`Generated ${d.files_count} files`); }
        }
      }
      await load();
    } catch (e) { toast.error("Generation failed"); } finally { setGenerating(false); }
  };

  const openFile = async (f) => {
    setActiveFile(f); setFileContent("");
    try { const full = await j(`/projects/${projectId}/files/${f.id}`); setFileContent(full.content); } catch { setFileContent("// failed to load"); }
  };

  const runQa = async () => { setBusy("qa"); try { setQa(await j(`/projects/${projectId}/qa`, "POST")); await load(); } catch (e) { toast.error(e.message); } setBusy(""); };

  const publishGithub = async () => {
    if (!ghToken.trim() && !ghConnected) { toast.error("Paste a GitHub token (repo scope), or connect GitHub in the Deploy panel."); return; }
    setBusy("gh");
    try { const r = await j(`/projects/${projectId}/github`, "POST", { token: ghToken || undefined, private: true }); setGhUrl(r.url);
      if (r.skipped_workflows?.length) toast.success("Pushed to GitHub!", { description: `Skipped ${r.skipped_workflows.length} CI workflow file(s) — your token lacks the 'workflow' scope. Reconnect with 'workflow' scope to include them.` });
      else toast.success("Pushed to GitHub!"); }
    catch (e) { toast.error(e.message); } setBusy("");
  };

  const download = async () => {
    try {
      const token = localStorage.getItem("arca_token");
      const res = await fetch(`${API}/projects/${projectId}/download`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${project.name}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
  };

  // Session still being verified
  if (user === undefined) return <Shell><div className="flex items-center gap-2 text-slate-400 py-20" data-testid="studio-session-loading"><Loader2 className="animate-spin" /> Checking your session…</div></Shell>;
  // Not authenticated — redirect effect is navigating to /login
  if (user === null) return <Shell><div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Redirecting to login…</div></Shell>;
  // Authenticated but the project failed to load (no access / not found / error)
  if (loadError) {
    return (
      <Shell right={<Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Studio</Link>}>
        <div className="max-w-lg mx-auto text-center py-24" data-testid="studio-load-error">
          <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-6"><AlertTriangle className="text-rose-400" /></div>
          <h1 className="font-display font-bold text-2xl mb-2">Can’t open this project</h1>
          <p className="text-slate-400 mb-8">{loadError}. It may not exist, or your account doesn’t have access to it.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/studio" data-testid="studio-back-btn" className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3.5 rounded-2xl font-semibold shadow-[0_0_30px_rgba(0,85,255,0.45)]"><ArrowLeft size={16} /> Back to Studio</Link>
            <button onClick={() => { setLoadError(null); load().catch((e) => setLoadError(e.message || "Could not load project")); }} data-testid="studio-retry-btn" className="inline-flex items-center gap-2 border border-white/15 px-5 py-3.5 rounded-2xl hover:border-[#0055FF] transition-colors duration-300">Try again</button>
          </div>
        </div>
      </Shell>
    );
  }
  // Authenticated, request in flight
  if (!project) return <Shell><div className="flex items-center gap-2 text-slate-400 py-20" data-testid="studio-project-loading"><Loader2 className="animate-spin" /> Loading project…</div></Shell>;

  const generated = project.status === "generated" || project.status === "qa_passed" || project.status === "qa_failed" || (project.files?.length > 0);

  return (
    <Shell right={<Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Studio</Link>}>
      <div className="mb-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#00F0FF]"><FileCode2 size={14} /> {project.status}</div>
      <h1 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight" data-testid="studio-project-name">{project.name}</h1>
      <p className="text-slate-400 mt-3 max-w-2xl">{arch.summary}</p>

      {/* Architecture decisions */}
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="studio-architecture">
        {[["frontend", arch.frontend?.framework], ["backend", arch.backend?.framework], ["database", arch.database?.engine], ["authentication", arch.authentication], ["payments", arch.payments], ["file_storage", arch.file_storage], ["notifications", arch.notifications], ["deployment", arch.deployment]].map(([k, v]) => {
          const Icon = ARCH_ICONS[k] || Cpu;
          return (
            <div key={k} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1"><Icon size={14} className="text-[#00F0FF]" /><span className="font-mono text-[10px] uppercase tracking-widest">{k.replace("_", " ")}</span></div>
              <p className="text-white text-sm font-medium">{v || "—"}</p>
            </div>
          );
        })}
      </div>

      {/* Milestones */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="studio-milestones">
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-4">Project Manager · Milestones</p>
        <div className="space-y-2">
          {milestones.map((m, mi) => (
            <div key={m.id || `ms-${mi}`} className="flex items-center gap-3">
              {m.status === "done" ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> : m.status === "in_progress" ? <Loader2 size={16} className="text-[#00F0FF] animate-spin shrink-0" /> : <div className="w-4 h-4 rounded-full border border-slate-600 shrink-0" />}
              <span className="text-sm text-white flex-1">{m.title}</span>
              <StatusBadge status={m.status} />
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3">
        {!generated && (
          <button onClick={generate} disabled={generating} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3.5 rounded-2xl font-semibold shadow-[0_0_30px_rgba(0,85,255,0.45)] disabled:opacity-40" data-testid="studio-generate-btn">
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} {generating ? "Building project…" : "Generate project"}
          </button>
        )}
        {generated && !generating && (
          <>
            <button onClick={generate} className="inline-flex items-center gap-2 border border-white/15 px-5 py-3.5 rounded-2xl hover:border-[#0055FF] transition-colors duration-300" data-testid="studio-resume-btn"><GitBranch size={16} /> Resume / rebuild</button>
            <button onClick={runQa} disabled={busy === "qa"} className="inline-flex items-center gap-2 border border-white/15 px-5 py-3.5 rounded-2xl hover:border-[#0055FF] transition-colors duration-300" data-testid="studio-qa-btn">{busy === "qa" ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Run QA</button>
            <button onClick={download} className="inline-flex items-center gap-2 bg-white text-black px-5 py-3.5 rounded-2xl font-semibold hover:bg-[#00F0FF] transition-colors duration-300" data-testid="studio-download-btn"><Download size={16} /> Download .zip</button>
          </>
        )}
      </div>

      {/* QA result */}
      {qa && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="studio-qa-result">
          <div className="flex items-center gap-3 mb-3"><span className="font-display font-bold text-2xl">{qa.score}<span className="text-slate-500 text-base">/100</span></span><span className={qa.passed ? "text-emerald-400" : "text-amber-400"}>{qa.passed ? "QA Passed" : "Needs work"}</span></div>
          <div className="space-y-1.5">
            {(qa.checks || []).map((c, i) => {
              const Icon = c.status === "pass" ? CheckCircle2 : c.status === "warn" ? AlertTriangle : XCircle;
              const col = c.status === "pass" ? "text-emerald-400" : c.status === "warn" ? "text-amber-400" : "text-red-400";
              return <div key={i} className="flex items-start gap-2 text-sm"><Icon size={14} className={`${col} mt-0.5 shrink-0`} /><span className="text-slate-300"><b>{c.area}:</b> {c.note}</span></div>;
            })}
          </div>
        </div>
      )}

      {/* File browser */}
      {generated && project.files?.length > 0 && (
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-4 max-h-[520px] overflow-auto" data-testid="studio-file-tree">
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5"><FolderTree size={13} /> {project.files.length} files</p>
            {project.files.map((f, fi) => (
              <button key={f.id || `f-${fi}`} onClick={() => openFile(f)} className={`block w-full text-left text-xs font-mono py-1.5 px-2 rounded hover:bg-white/5 truncate ${activeFile?.id === f.id ? "text-[#00F0FF] bg-white/5" : "text-slate-400"}`} data-testid={`studio-file-${f.path.replace(/[^a-z0-9]/gi, '-')}`}>{f.path}</button>
            ))}
          </div>
          <div className="md:col-span-2 rounded-2xl border border-white/10 bg-[#050505] overflow-hidden">
            <div className="px-4 py-2.5 border-b border-white/10 font-mono text-xs text-slate-400">{activeFile?.path || "Select a file"}</div>
            <pre className="p-4 overflow-auto max-h-[480px] text-[11px] font-mono text-slate-300 leading-relaxed" data-testid="studio-file-content">{fileContent || "// click a file to view its contents"}</pre>
          </div>
        </div>
      )}

      {/* GitHub publish */}
      {generated && (
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="studio-github">
          <p className="font-display font-bold text-lg mb-2 flex items-center gap-2"><Github size={18} /> Publish to GitHub</p>
          {ghUrl ? (
            <a href={ghUrl} target="_blank" rel="noreferrer" className="text-[#00F0FF] text-sm" data-testid="studio-github-url">{ghUrl}</a>
          ) : ghConnected ? (
            <div className="mt-2">
              <button onClick={publishGithub} disabled={busy === "gh"} className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 rounded-xl font-semibold hover:bg-[#00F0FF] disabled:opacity-40 transition-colors duration-300" data-testid="studio-github-connected-btn">{busy === "gh" ? <Loader2 size={16} className="animate-spin" /> : <Github size={16} />} Publish with connected GitHub</button>
              <p className="text-emerald-400 text-xs mt-2">Using your connected GitHub account.</p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <input value={ghToken} onChange={(e) => setGhToken(e.target.value)} type="password" placeholder="GitHub token (repo scope)" className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0055FF]" data-testid="studio-github-token" />
              <button onClick={publishGithub} disabled={busy === "gh"} className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 rounded-xl font-semibold hover:bg-[#00F0FF] disabled:opacity-40 transition-colors duration-300" data-testid="studio-github-btn">{busy === "gh" ? <Loader2 size={16} className="animate-spin" /> : <Github size={16} />} Create repo & push</button>
            </div>
          )}
          <p className="text-slate-600 text-xs mt-2">Connect GitHub once in the Deploy panel for one-click publishing, or paste a token used once and never stored.</p>
        </div>
      )}

      {/* One-Click Global Publish (headline action) */}
      {generated && <PublishWorldwide projectId={projectId} />}

      {/* AI Project Manager (Phase 11) */}
      {generated && <ProjectPlanPanel projectId={projectId} initialPlan={project.plan} />}

      {/* AI Architecture Review (Phase 10) — pre-deploy audit */}
      {generated && <ArchReviewPanel projectId={projectId} initialReview={project.review} />}

      {/* Global Launch Assistant (Phase 15) — global readiness score */}
      {generated && <GlobalReadinessPanel projectId={projectId} initialReadiness={project.readiness} />}

      {/* Real deployment integrations */}
      {generated && <DeployPanel projectId={projectId} githubUrl={ghUrl} />}
    </Shell>
  );
}
